import type { SeriesFacets, SeriesFilterSelection } from '../catalog-series-filters'

export interface CatalogSeriesFilterSidebarProps {
  readonly facets: SeriesFacets
  readonly selection: SeriesFilterSelection
  readonly onChange: (selection: SeriesFilterSelection) => void
}

/** The sidebar's own all-cleared value — matches
 * `CatalogSeriesConfigurePage`'s `EMPTY_SELECTION`, kept as a local literal
 * (rather than a shared export) since this is the only place that needs to
 * construct one from scratch; every other consumer only ever reads/updates
 * an existing `SeriesFilterSelection` a caller passed in. */
const EMPTY_SELECTION: SeriesFilterSelection = {
  standard: false,
  configurable: false,
  ranges: new Map(),
  enums: new Map(),
}

/**
 * Surface A's series filter sidebar (UID-34 / ADR-0099 §5a) — backed by
 * EXACTLY the two real facets `deriveSeriesFacets` derives: the
 * Standard/Configurable `sourceable.kind` toggle (always rendered, two
 * independently-checkable boxes — NOT the single-select radio bar
 * `CategoryFilterBar.tsx` uses for a different screen) and the per-dimension
 * `range`/`enum` facets unioned across the series' rows. Everything else in
 * the fuller mock is omitted entirely — no disabled stub sections — but see
 * `deriveSeriesFacets`' doc comment in `../catalog-series-filters` for WHY,
 * which is not one reason but two: End Type/End Machining and a real Stock
 * Availability are genuinely unbacked, while Material / Finish / Metric-Inch
 * ARE real per-item facets on `configAttributes.specs` and are omitted here on
 * granularity + coverage-asymmetry grounds (ADR-0111, correcting ADR-0099
 * Gap 1's blanket "no backing data" claim).
 */
export function CatalogSeriesFilterSidebar({
  facets,
  selection,
  onChange,
}: CatalogSeriesFilterSidebarProps) {
  return (
    <nav className="catalog-series-filter-sidebar" aria-label="Filter configurations">
      <div className="catalog-series-filter-header">
        <button
          type="button"
          className="catalog-series-filter-clear"
          onClick={() => onChange(EMPTY_SELECTION)}
        >
          Clear all
        </button>
      </div>
      <fieldset className="catalog-series-filter-section">
        <legend className="catalog-series-filter-legend">Standard / Configurable</legend>
        <label className="catalog-series-filter-checkbox">
          <input
            type="checkbox"
            checked={selection.standard}
            onChange={(event) => onChange({ ...selection, standard: event.target.checked })}
          />
          Standard Dimensions
        </label>
        <label className="catalog-series-filter-checkbox">
          <input
            type="checkbox"
            checked={selection.configurable}
            onChange={(event) => onChange({ ...selection, configurable: event.target.checked })}
          />
          Configurable Dimensions
        </label>
      </fieldset>

      {facets.dimensionFacets.map((facet) =>
        facet.kind === 'range' ? (
          <fieldset key={facet.fieldName} className="catalog-series-filter-section">
            <legend className="catalog-series-filter-legend">{facet.fieldName}</legend>
            <div className="catalog-series-filter-range">
              <span className="catalog-series-filter-range-affix" aria-hidden="true">
                Min
              </span>
              <input
                type="number"
                inputMode="decimal"
                className="catalog-series-filter-range-input"
                aria-label={`${facet.fieldName} min`}
                min={facet.min}
                max={facet.max}
                value={selection.ranges.get(facet.fieldName)?.min ?? ''}
                placeholder={String(facet.min)}
                onChange={(event) => {
                  const parsed = Number(event.target.value)
                  const current = selection.ranges.get(facet.fieldName) ?? {
                    min: facet.min,
                    max: facet.max,
                  }
                  const nextRanges = new Map(selection.ranges)
                  if (event.target.value.trim() === '' || !Number.isFinite(parsed)) {
                    nextRanges.delete(facet.fieldName)
                  } else {
                    nextRanges.set(facet.fieldName, { ...current, min: parsed })
                  }
                  onChange({ ...selection, ranges: nextRanges })
                }}
              />
              <span className="catalog-series-filter-range-affix" aria-hidden="true">
                Max
              </span>
              <input
                type="number"
                inputMode="decimal"
                className="catalog-series-filter-range-input"
                aria-label={`${facet.fieldName} max`}
                min={facet.min}
                max={facet.max}
                value={selection.ranges.get(facet.fieldName)?.max ?? ''}
                placeholder={String(facet.max)}
                onChange={(event) => {
                  const parsed = Number(event.target.value)
                  const current = selection.ranges.get(facet.fieldName) ?? {
                    min: facet.min,
                    max: facet.max,
                  }
                  const nextRanges = new Map(selection.ranges)
                  if (event.target.value.trim() === '' || !Number.isFinite(parsed)) {
                    nextRanges.delete(facet.fieldName)
                  } else {
                    nextRanges.set(facet.fieldName, { ...current, max: parsed })
                  }
                  onChange({ ...selection, ranges: nextRanges })
                }}
              />
            </div>
          </fieldset>
        ) : (
          <fieldset key={facet.fieldName} className="catalog-series-filter-section">
            <legend className="catalog-series-filter-legend">{facet.fieldName}</legend>
            {facet.values.map((value) => {
              const checkedValues = selection.enums.get(facet.fieldName) ?? new Set<number>()
              const checked = checkedValues.has(value)
              return (
                <label key={value} className="catalog-series-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      const nextValues = new Set(checkedValues)
                      if (event.target.checked) {
                        nextValues.add(value)
                      } else {
                        nextValues.delete(value)
                      }
                      const nextEnums = new Map(selection.enums)
                      nextEnums.set(facet.fieldName, nextValues)
                      onChange({ ...selection, enums: nextEnums })
                    }}
                  />
                  {value}
                </label>
              )
            })}
          </fieldset>
        ),
      )}
    </nav>
  )
}
